import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, AlertCircle } from "lucide-react";
import { db } from "./admin/../../firebase";
import { collection, onSnapshot } from "firebase/firestore";

// --- List of current static partners (for your reference) ---
// [
//   { name: "Prime Video", logo: "/svg/Prime Video.svg" },
//   { name: "Netflix", logo: "/svg/Netflix.svg" },
//   { name: "Tesco", logo: "/svg/Tesco.svg" },
//   { name: "Sky Bingo", logo: "/svg/Sky Bingo.svg" },
//   { name: "BBH", logo: "/svg/BBH.svg" },
//   { name: "BT", logo: "/svg/BT.svg" },
//   { name: "McCann", logo: "/svg/McCann.svg" },
//   { name: "Liquona", logo: "/svg/Liquona.svg" },
//   { name: "Primark", logo: "/svg/Primark.svg" },
//   { name: "Hyve", logo: "/svg/Hyve.svg" },
//   { name: "Robinsons", logo: "/svg/Robinsons.svg" },
//   { name: "Thomas Cook", logo: "/svg/Thomas Cook.svg" },
//   { name: "Courtney", logo: "/svg/Courtney.svg" },
//   { name: "Felt", logo: "/svg/Felt.svg" },
//   { name: "CIF", logo: "/svg/CIF.svg" },
// ]
// -----------------------------------------------------------

const PartnersSection = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageLoadStates, setImageLoadStates] = useState({});

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, "partners"),
      (snapshot) => {
        const firestorePartners = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPartners(firestorePartners);
        setLoading(false);
        // Initialize image load states
        const initialLoadStates = {};
        firestorePartners.forEach((partner) => {
          initialLoadStates[partner.id] = "loading";
        });
        setImageLoadStates(initialLoadStates);
      },
      (err) => {
        console.error("Error loading partners:", err);
        setError("Failed to load partners");
        setPartners([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Handle image load success
  const handleImageLoad = (partnerId) => {
    setImageLoadStates((prev) => ({
      ...prev,
      [partnerId]: "loaded",
    }));
  };

  // Handle image load error
  const handleImageError = (partnerId) => {
    setImageLoadStates((prev) => ({
      ...prev,
      [partnerId]: "error",
    }));
  };

  return (
    <section className="bg-[#111111] relative py-32 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgb(1,178,128)_1px,_transparent_0)] [background-size:40px_40px] opacity-5" />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header Section */}
        <motion.div
          className="text-center max-w-3xl mx-auto mb-24"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="text-primary-green mb-4 block">OUR PARTNERS</span>
          <h2 className="text-5xl font-light text-white mb-8">
            Trusted By Industry
            <br />
            <span className="font-bold">Leading Brands</span>
          </h2>

          {/* Decorative Elements */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-primary-green/50" />
            <span className="text-white/50">
              {partners.length}+ Global Partners
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-primary-green/50" />
          </div>
        </motion.div>

        {/* Partners Grid */}
        <div className="relative">
          {/* Background Glow */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary-green/5 via-primary-purple/5 to-transparent rounded-3xl blur-3xl" />

          {loading ? (
            <div className="text-center text-white py-12">
              <div className="inline-flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-primary-green border-t-transparent rounded-full animate-spin" />
                <span>Loading partners...</span>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center gap-3 text-red-400">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </div>
          ) : (
            <div className="relative grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {partners.map((partner, index) => {
                const loadState = imageLoadStates[partner.id] || "loading";

                return (
                  <motion.div
                    key={partner.id || partner.name || index}
                    className="group relative"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    {/* Card with Hover Effect */}
                    <div className="relative h-full">
                      {/* Glowing Border */}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary-green/20 to-primary-purple/20 rounded-xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      {/* Content Container */}
                      <div className="relative bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 group-hover:border-white/20 transition-all duration-300">
                        <div className="aspect-video flex items-center justify-center relative">
                          {/* Loading state */}
                          {loadState === "loading" && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-6 h-6 border-2 border-primary-green/30 border-t-primary-green rounded-full animate-spin" />
                            </div>
                          )}

                          {/* Error state */}
                          {loadState === "error" && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/5 rounded">
                              <div className="text-center">
                                <AlertCircle className="w-6 h-6 text-white/30 mx-auto mb-1" />
                                <span className="text-xs text-white/30">
                                  Logo unavailable
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Partner Logo */}
                          <img
                            src={partner.logo || ""}
                            alt={`${partner.name} logo`}
                            className={`w-full h-full object-contain transition-opacity duration-300 ${
                              loadState === "loaded"
                                ? "opacity-70 group-hover:opacity-100"
                                : "opacity-0"
                            }`}
                            loading="lazy"
                            onLoad={() => handleImageLoad(partner.id)}
                            onError={() => handleImageError(partner.id)}
                            style={{ backgroundColor: "transparent" }}
                          />
                        </div>

                        {/* Partner name overlay on hover for better UX */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-b-xl p-3">
                          <span className="text-white text-sm font-medium">
                            {partner.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* CTA Section */}
        <motion.div
          className="relative z-10 text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.button
            className="group inline-flex items-center gap-3 px-8 py-4 bg-primary-green/10 text-primary-green border border-primary-green/20 rounded-full font-medium hover:bg-primary-green hover:text-white transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => (window.location.href = "/client-work")}
          >
            VIEW PROJECTS
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnersSection;
